import { useState } from 'react';
import { Megaphone, Copy, Check, Pencil, Save, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Program } from '@/data/hooks/usePrograms';

type FieldKey = 'best_for' | 'talk_track' | 'objection_handling' | 'urgency_hook';

const FIELDS: { key: FieldKey; label: string; placeholder: string }[] = [
  { key: 'best_for', label: 'Best For', placeholder: 'e.g. Payment-focused SUV buyers' },
  { key: 'talk_track', label: 'Talk Track', placeholder: 'Customer-facing one-liner reps can use verbatim.' },
  { key: 'objection_handling', label: 'Objection Handling', placeholder: 'Common pushback → response.' },
  { key: 'urgency_hook', label: 'Urgency Hook', placeholder: 'Why now? What changes if they wait?' },
];

export function SalesPlaybookCard({
  program, canEdit, isDemo, onSaved,
}: {
  program: Program;
  canEdit: boolean;
  isDemo: boolean;
  onSaved?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<FieldKey, string>>({
    best_for: program.best_for ?? '',
    talk_track: program.talk_track ?? '',
    objection_handling: program.objection_handling ?? '',
    urgency_hook: program.urgency_hook ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<FieldKey | null>(null);

  const hasAny = FIELDS.some(f => (program as any)[f.key]);

  const copy = async (key: FieldKey, val: string) => {
    await navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const save = async () => {
    if (isDemo) {
      toast.info('Demo mode — sign in to save talking points.');
      setEditing(false);
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('programs').update(draft).eq('id', program.id);
    setSaving(false);
    if (error) { toast.error('Could not save'); return; }
    toast.success('Playbook saved');
    setEditing(false);
    onSaved?.();
  };

  return (
    <Card className="border-border/60 bg-card/80 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">Sales Playbook</h2>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" /> {hasAny ? 'Edit' : 'Add talking points'}
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </Button>
          </div>
        )}
      </div>

      {!hasAny && !editing && (
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? 'No talking points yet. Add a Best For / Talk Track to help reps sell from this program.'
            : 'No talking points have been added for this program yet.'}
        </p>
      )}

      <div className="space-y-4">
        {FIELDS.map(f => {
          const val = editing ? draft[f.key] : ((program as any)[f.key] as string | null | undefined);
          if (!editing && !val) return null;
          return (
            <div key={f.key}>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{f.label}</div>
                {!editing && val && (
                  <button
                    onClick={() => copy(f.key, val)}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    {copied === f.key ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied === f.key ? 'Copied' : 'Copy'}
                  </button>
                )}
              </div>
              {editing ? (
                <Textarea
                  value={draft[f.key]}
                  onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  rows={2}
                  className="text-sm"
                />
              ) : (
                <p className="text-sm leading-relaxed">{val}</p>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}